import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Alert, CircularProgress, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TravelRepository, {
    SCENARIO_APPROVAL,
    SCENARIO_ATTESTATION,
    SCENARIO_NORMAL,
    SCENARIO_SAME_ADDRESS,
    TravelInfo,
    modeUsesSubscription,
} from "../../../Repositories/TravelRepository";
import TransportSubscriptionRepository from "../../../Repositories/TransportSubscriptionRepository";
import { ClientDetailTravelMode } from "../../../Repositories/ClientDetailRepository";
import Teachr from "../../../Objects/models/Teachr";
import ButtonField from "../../../Components/Ui/ButtonField";
import ComplianceAlert from "../../DeclarationCourse/Travel/ComplianceAlert";
import AttestationModal from "../../DeclarationCourse/Travel/AttestationModal";
import TransportSubscriptionUpload from "../../DeclarationCourse/Travel/TransportSubscriptionUpload";
import { useTailwindBrand } from "../../../Components/shared";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";
import { DetailSection, InfoRow } from "./DetailSection";
import { formatDate, formatKm } from "./clientDetailFormat";

interface Props {
    /** Professeur connecté (adresse et identité pré-remplies sur l'attestation). */
    teachr: Teachr;
    teachrId: number;
    clientId: number;
    clientName: string;
    /** Mode enregistré (fiche client) : connu même sans attestation signée. */
    travelMode: ClientDetailTravelMode | null;
    coveredBySubscriptionCount: number;
}

const SOURCE_LABELS: Record<string, string> = {
    google: "trajet routier",
    haversine: "à vol d'oiseau, estimation",
};

/**
 * Explication en clair du traitement des déclarations selon la distance.
 * Au-delà du seuil haut, l'encart « Preuve de déplacement requise » prend le
 * relais (texte adapté au mode de déplacement).
 */
const scenarioText = (info: TravelInfo): { severity: "success" | "info" | "warning"; text: string } | null => {
    switch (info.scenario) {
        case SCENARIO_SAME_ADDRESS:
            return {
                severity: "warning",
                text: `Votre adresse est identique à celle de ${getActiveClientBrandConfig("", "l'", "ce ")} : chaque déclaration part en approbation administrateur.`,
            };
        case SCENARIO_APPROVAL:
            return null;
        case SCENARIO_ATTESTATION:
            return {
                severity: "info",
                text: `Entre ${info.thresholdLow} et ${info.thresholdHigh} km : une attestation de déplacement sur l'honneur signée est requise.`,
            };
        case SCENARIO_NORMAL:
        default:
            return {
                severity: "success",
                text: info.distanceKm == null
                    ? "Distance non calculée : vos déclarations suivent le traitement normal."
                    : `Moins de ${info.thresholdLow} km : vos déclarations passent sans justificatif.`,
            };
    }
};

/**
 * Déplacement du professeur jusqu'au client : distance et scénario, mode de
 * transport, attestation courante (et motif si elle n'est plus valable),
 * couverture par l'abonnement de transport du professeur.
 */
const ClientTravelSection = ({ teachr, teachrId, clientId, clientName, travelMode, coveredBySubscriptionCount }: Props) => {
    const { classes, brandConfig } = useTailwindBrand();
    const [info, setInfo] = useState<TravelInfo | null>(null);
    // Attestation manquante ou plus valable (adresse modifiée…) alors que le
    // scénario l'exige : signature possible depuis la fiche, sans attendre la
    // prochaine déclaration. Rien n'est proposé quand elle n'est pas requise.
    const [signing, setSigning] = useState(false);
    const [justSigned, setJustSigned] = useState(false);
    // Abonnement de transport du couple prof/client, déposable depuis la fiche
    const [uploadingSubscription, setUploadingSubscription] = useState(false);
    const [subscriptionSaved, setSubscriptionSaved] = useState(false);
    // Abonnement « à confirmer » après un changement d'adresse : confirmation en un clic
    const [confirmingSubscription, setConfirmingSubscription] = useState(false);
    const [subscriptionConfirmed, setSubscriptionConfirmed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    // P7 : le mode de déplacement se modifie depuis la fiche (référence pour
    // la règle de cohérence mode / distance)
    const [editing, setEditing] = useState(false);
    const [pendingMode, setPendingMode] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [saved, setSaved] = useState(false);

    const loadInfo = React.useCallback((silent = false) => {
        let cancelled = false;
        if (!silent) setLoading(true);
        setFailed(false);
        TravelRepository.getTravelInfo(teachrId, clientId)
            .then((response) => {
                if (!cancelled) setInfo(response);
            })
            .catch(() => {
                if (!cancelled && !silent) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [teachrId, clientId]);

    useEffect(() => loadInfo(), [loadInfo]);

    const startEditing = () => {
        setPendingMode(info?.travelMode?.mode ?? (travelMode?.isValid && travelMode.mode ? travelMode.mode : ""));
        setSaveError("");
        setSaved(false);
        setEditing(true);
    };

    const handleSaveMode = async () => {
        if (!pendingMode) {
            setSaveError("Choisissez un mode de déplacement.");
            return;
        }
        setSaving(true);
        setSaveError("");
        try {
            const result = await TravelRepository.setTravelMode(teachrId, clientId, pendingMode);
            const usesSubscription = modeUsesSubscription(pendingMode);
            setInfo((prev) => prev ? { ...prev, travelMode: result.travelMode, complianceAlert: result.complianceAlert ?? prev.complianceAlert, travelModeUsesSubscription: usesSubscription } : prev);
            setEditing(false);
            setSaved(true);
            // l'attestation a pu devenir caduque : on relit l'état complet
            loadInfo(true);
            // Transports en commun ou train, sans abonnement pour ce trajet :
            // on propose de le renseigner tout de suite (facultatif sous le
            // seuil haut, requis au-delà ou un billet à chaque déclaration).
            const hasSubscription = !!info?.transportSubscription || !!(info?.transportSubscriptions ?? []).some((sub) => sub.needsConfirmation);
            if (usesSubscription && info && !hasSubscription) {
                const required = info.scenario === SCENARIO_APPROVAL;
                const answer = await Swal.fire({
                    icon: "question",
                    title: `Vous voyagez en ${(info.travelModes?.[pendingMode] ?? pendingMode).toLowerCase()}`,
                    html: required
                        ? `À plus de ${info.thresholdHigh} km, une preuve est demandée à chaque ${getActivePrestationBrandConfig()}. Renseignez votre abonnement une fois : il servira de preuve pour toutes vos déclarations avec <b>${clientName}</b>. Sinon, un billet vous sera demandé à chaque déclaration.`
                        : `Vous pouvez renseigner votre abonnement (Navigo, TER...) pour vos trajets jusqu'à <b>${clientName}</b>. C'est facultatif à cette distance.`,
                    showCancelButton: true,
                    confirmButtonText: "Renseigner mon abonnement",
                    cancelButtonText: "Plus tard",
                });
                if (answer.isConfirmed) setUploadingSubscription(true);
            }
        } catch (e) {
            console.warn("Enregistrement du mode de déplacement impossible", e);
            setSaveError("L'enregistrement a échoué, réessayez plus tard.");
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmSubscription = async (subscriptionId: number) => {
        setConfirmingSubscription(true);
        try {
            await TransportSubscriptionRepository.confirm(subscriptionId);
            setSubscriptionConfirmed(true);
            loadInfo(true);
        } catch (e) {
            console.warn("Confirmation de l'abonnement impossible", e);
            await Swal.fire({ icon: "error", title: "Confirmation impossible", text: "Réessayez plus tard.", confirmButtonText: "J'ai compris" });
        } finally {
            setConfirmingSubscription(false);
        }
    };

    const renderTravelMode = () => {
        if (info?.travelMode) {
            return info.travelMode.distanceKm != null
                ? `${info.travelMode.label} (${formatKm(info.travelMode.distanceKm)})`
                : info.travelMode.label;
        }
        if (travelMode?.isValid) {
            return travelMode.distanceKm != null ? `${travelMode.label} (${formatKm(travelMode.distanceKm)})` : travelMode.label;
        }
        if (info?.attestation?.travelModeLabel) return info.attestation.travelModeLabel;
        if (travelMode) {
            return (
                <>
                    {travelMode.label} <span className="text-amber-700">(plus valable{travelMode.invalidationReason ? ` : ${travelMode.invalidationReason}` : ""})</span>
                </>
            );
        }
        return "Non renseigné";
    };

    const renderSubscription = () => {
        if (!info) return "-";
        const current = info.transportSubscription;
        if (current) {
            const scope = current.clientId == null ? " (tous vos clients)" : "";
            return `${current.typeLabel}, valide jusqu'au ${formatDate(current.validUntil, "DD/MM/YYYY")}${scope}`;
        }
        if (pendingSubscription) {
            return <span className="text-amber-700">{pendingSubscription.typeLabel}, à confirmer</span>;
        }
        const next = info.transportSubscriptions?.[0];
        if (next) {
            return `${next.typeLabel}, valide du ${formatDate(next.validFrom, "DD/MM/YYYY")} au ${formatDate(next.validUntil, "DD/MM/YYYY")}`;
        }
        if (info.transportSubscriptionReason === "other_client") {
            return `Aucun pour ${getActiveClientBrandConfig("", "cet élève", "ce client")} (vos abonnements sont rattachés à d'autres ${getActiveClientBrandConfig("s")})`;
        }
        return "Aucun abonnement renseigné";
    };

    const scenario = info ? scenarioText(info) : null;
    const attestationMissing = !!info && info.scenario === SCENARIO_ATTESTATION && !info.attestation;
    // Mode de référence du couple : connu même sans attestation
    const currentMode = info?.travelMode?.mode ?? (travelMode?.isValid && travelMode.mode ? travelMode.mode : null);
    // L'abonnement n'a de sens qu'en transports en commun ou en train
    const usesSubscription = !!info && (info.travelModeUsesSubscription ?? modeUsesSubscription(currentMode));
    const coveringSubscription = info?.transportSubscription ?? null;
    // À confirmer après un changement d'adresse (la sienne ou celle du client)
    const pendingSubscription = (info?.transportSubscriptions ?? []).find((sub) => sub.needsConfirmation) ?? null;
    // Plus de 160 km : preuve (abonnement ou billet) exigée à chaque déclaration
    const proofRequired = !!info && info.scenario === SCENARIO_APPROVAL;
    const clientWord = getActiveClientBrandConfig("", "cet élève", "ce client");

    const subscriptionButtons = (
        <span className="inline-flex flex-wrap gap-1">
            {pendingSubscription && (
                <ButtonField
                    size="small"
                    variant="contained"
                    label="Confirmer l'abonnement"
                    loading={confirmingSubscription}
                    disabled={confirmingSubscription}
                    onClick={() => handleConfirmSubscription(pendingSubscription.id)}
                />
            )}
            <ButtonField
                size="small"
                variant={pendingSubscription || coveringSubscription ? "outlined" : "contained"}
                label={pendingSubscription || coveringSubscription
                    ? "Déposer un autre abonnement"
                    : (proofRequired ? "Déposer mon abonnement" : "Renseigner mon abonnement")}
                onClick={() => setUploadingSubscription(true)}
            />
        </span>
    );

    /** Encart « Preuve par abonnement » : seulement en transports en commun ou en train. */
    const renderSubscriptionBlock = () => {
        if (!info || !usesSubscription) return null;
        if (pendingSubscription) {
            return (
                <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }} action={subscriptionButtons}>
                    <strong>Preuve par abonnement : à confirmer.</strong> {pendingSubscription.confirmationReason || "Une adresse a été modifiée"}.
                    Votre abonnement {pendingSubscription.typeLabel} (valide jusqu'au {formatDate(pendingSubscription.validUntil, "DD/MM/YYYY")}) ne vaut pas preuve tant que vous ne l'avez pas confirmé.
                </Alert>
            );
        }
        if (coveringSubscription) {
            return (
                <Alert severity="success" variant="outlined" sx={{ mb: 1.5, fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }} action={subscriptionButtons}>
                    <strong>Preuve par abonnement.</strong> Votre abonnement {coveringSubscription.typeLabel} couvre ce trajet jusqu'au {formatDate(coveringSubscription.validUntil, "DD/MM/YYYY")}
                    {coveringSubscription.clientId == null ? " (abonnement valable pour tous vos clients)" : ""}.
                    {proofRequired
                        ? " Il sert de preuve à chaque déclaration, l'approbation par notre équipe reste requise."
                        : " Il n'est pas exigé à cette distance, il est conservé pour information."}
                </Alert>
            );
        }
        return (
            <Alert severity={proofRequired ? "warning" : "info"} variant="outlined" sx={{ mb: 1.5, fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }} action={subscriptionButtons}>
                <strong>Preuve par abonnement.</strong>{" "}
                {proofRequired
                    ? `Aucun abonnement renseigné pour ${clientWord} : déposez-le une seule fois, sinon un billet vous sera demandé à chaque déclaration.`
                    : `Aucun abonnement renseigné pour ${clientWord} (facultatif en dessous de ${info.thresholdHigh} km).`}
                {info.transportSubscriptionReason === "other_client" && ` Vos abonnements existants sont rattachés à d'autres ${getActiveClientBrandConfig("s")}.`}
            </Alert>
        );
    };

    /** Encart « Preuve de déplacement requise » : seulement au-delà du seuil haut. */
    const renderProofRequiredBlock = () => {
        if (!info || !proofRequired) return null;
        let text: React.ReactNode;
        if (usesSubscription) {
            text = coveringSubscription
                ? <>Plus de {info.thresholdHigh} km : chaque déclaration part en approbation par notre équipe, votre abonnement sert de preuve.</>
                : <>Plus de {info.thresholdHigh} km : chaque déclaration part en approbation par notre équipe. Déposez votre abonnement ci-dessus une seule fois, sinon un billet vous sera demandé à chaque déclaration.</>;
        } else if (currentMode) {
            text = <>Plus de {info.thresholdHigh} km : chaque déclaration part en approbation par notre équipe. Un justificatif de déplacement (billet, péage, carburant...) vous sera demandé à chaque déclaration.</>;
        } else {
            text = <>Plus de {info.thresholdHigh} km : chaque déclaration part en approbation par notre équipe avec une preuve de déplacement. Renseignez votre mode de déplacement : en transports en commun ou en train, un abonnement peut servir de preuve.</>;
        }
        return (
            <Alert
                severity="warning"
                variant="outlined"
                sx={{ mb: 1.5, fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }}
                action={!currentMode && !editing ? <ButtonField size="small" variant="outlined" label="Renseigner le mode" onClick={startEditing} /> : undefined}
            >
                <strong>Preuve de déplacement requise.</strong> {text}
            </Alert>
        );
    };

    return (
        <DetailSection
            title="Déplacement"
            icon={<DirectionsCarFilledOutlinedIcon fontSize="small" />}
            subtitle={`Vos trajets jusqu'à ${getActiveClientBrandConfig("", "l'", "ce ")} pour vos ${getActivePrestationBrandConfig("s")}.`}
            action={!loading && !failed ? (
                <ButtonField
                    size="small"
                    variant="outlined"
                    label={editing ? "Annuler" : "Modifier le mode"}
                    disabled={saving}
                    onClick={() => (editing ? setEditing(false) : startEditing())}
                />
            ) : undefined}
        >
            {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <CircularProgress size={16} /> Calcul des informations de déplacement...
                </div>
            )}

            {!loading && failed && (
                <p className="text-sm text-gray-500">Les informations de déplacement sont indisponibles pour le moment.</p>
            )}

            {!loading && info && (
                <>
                    {/* P7 : anomalie mode / distance, non bloquante */}
                    <ComplianceAlert
                        alert={info.complianceAlert}
                        onConfirmed={(updated) => setInfo((prev) => prev ? { ...prev, complianceAlert: updated } : prev)}
                        className="mb-3"
                    />

                    {editing && (
                        <div className="border border-gray-200 rounded-md p-3 mb-3 bg-gray-50">
                            <p className={`text-sm font-semibold mb-1 ${classes.primaryText}`}>Mode de déplacement habituel</p>
                            <RadioGroup row value={pendingMode} onChange={(e) => setPendingMode(e.target.value)}>
                                {Object.entries(info.travelModes ?? {}).map(([value, label]) => (
                                    <FormControlLabel
                                        key={value}
                                        value={value}
                                        control={<Radio size="small" />}
                                        label={<span className="text-sm">{label}</span>}
                                    />
                                ))}
                            </RadioGroup>
                            {info.attestation && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Changer de mode rend votre attestation de déplacement caduque : une nouvelle signature vous sera demandée à la prochaine déclaration.
                                </p>
                            )}
                            {saveError && <p className="text-xs text-red-600 mt-1">{saveError}</p>}
                            <div className="flex justify-end mt-2">
                                <ButtonField size="small" variant="contained" label="Enregistrer" loading={saving} disabled={saving} onClick={handleSaveMode} />
                            </div>
                        </div>
                    )}
                    {saved && !editing && (
                        <p className="text-xs text-emerald-700 mb-2">Mode de déplacement enregistré.</p>
                    )}

                    {/* Attestation requise mais absente ou invalidée : signature possible ici */}
                    {attestationMissing && (
                        <Alert
                            severity="warning"
                            variant="outlined"
                            sx={{ mb: 1.5, fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }}
                            action={(
                                <ButtonField
                                    size="small"
                                    variant="contained"
                                    label="Signer l'attestation"
                                    onClick={() => setSigning(true)}
                                />
                            )}
                        >
                            {info.addressChangedSinceAttestation ? (
                                <>
                                    Votre attestation de déplacement pour {getActiveClientBrandConfig("", "cet ", "ce ")} n'est plus valable
                                    {info.invalidationReason ? <> : <strong>{info.invalidationReason}</strong></> : null}.
                                    Signez-en une nouvelle maintenant, ou à votre prochaine déclaration.
                                </>
                            ) : (
                                <>
                                    Aucune attestation de déplacement signée pour {getActiveClientBrandConfig("", "cet ", "ce ")}.
                                    Vous pouvez la signer maintenant, ou à votre prochaine déclaration.
                                </>
                            )}
                        </Alert>
                    )}
                    {justSigned && (
                        <p className="text-xs text-emerald-700 mb-2">Attestation signée et enregistrée.</p>
                    )}

                    {scenario && (
                        <Alert severity={scenario.severity} variant="outlined" sx={{ mb: 1.5, fontSize: "0.8rem", fontFamily: brandConfig.fontFamily }}>
                            {scenario.text}
                        </Alert>
                    )}
                    {renderSubscriptionBlock()}
                    {renderProofRequiredBlock()}
                    {subscriptionConfirmed && (
                        <p className="text-xs text-emerald-700 mb-2">Abonnement confirmé : il sert à nouveau de preuve pour ce trajet.</p>
                    )}

                    <div>
                        <InfoRow
                            label="Distance"
                            value={info.distanceKm != null
                                ? `${formatKm(info.distanceKm)}${info.source && SOURCE_LABELS[info.source] ? ` (${SOURCE_LABELS[info.source]})` : ""}`
                                : "Non calculée"}
                        />
                        <InfoRow label="Mode de déplacement" value={renderTravelMode()} />
                        <InfoRow
                            label="Attestation"
                            value={info.attestation ? (
                                <span className="inline-flex flex-wrap items-center justify-end gap-x-2">
                                    <span>Signée le {formatDate(info.attestation.signedAt, "DD/MM/YYYY")}</span>
                                    {info.attestation.url && (
                                        <a
                                            href={info.attestation.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`${classes.primaryText} inline-flex items-center gap-0.5 underline`}
                                        >
                                            <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} /> PDF
                                        </a>
                                    )}
                                </span>
                            ) : info.addressChangedSinceAttestation
                                ? <span className="text-amber-700">Plus valable{info.invalidationReason ? ` : ${info.invalidationReason}` : ""}</span>
                                : info.scenario === SCENARIO_ATTESTATION
                                    ? "À signer à la prochaine déclaration"
                                    : "Non requise"}
                        />
                        {(usesSubscription || coveringSubscription || pendingSubscription) && (
                            <InfoRow label="Abonnement de transport" value={renderSubscription()} />
                        )}
                        {coveredBySubscriptionCount > 0 && (
                            <InfoRow
                                label="Preuve par abonnement"
                                value={`Utilisé pour ${coveredBySubscriptionCount} ${coveredBySubscriptionCount > 1 ? getActivePrestationBrandConfig("s") : getActivePrestationBrandConfig()}`}
                            />
                        )}
                    </div>

                    {subscriptionSaved && (
                        <p className="text-xs text-emerald-700 mt-2">Abonnement enregistré pour {getActiveClientBrandConfig("", "cet ", "ce ")}.</p>
                    )}
                </>
            )}

            {uploadingSubscription && (
                <TransportSubscriptionUpload
                    open
                    teachr={teachr}
                    clientId={clientId}
                    clientName={clientName}
                    onClose={() => setUploadingSubscription(false)}
                    onSaved={() => {
                        setUploadingSubscription(false);
                        setSubscriptionSaved(true);
                        setSubscriptionConfirmed(false);
                        loadInfo(true);
                    }}
                />
            )}

            {info && signing && (
                <AttestationModal
                    open={signing}
                    teachr={teachr}
                    clientId={clientId}
                    clientName={clientName}
                    travelInfo={info}
                    onSigned={() => {
                        setSigning(false);
                        setJustSigned(true);
                        loadInfo(true);
                    }}
                    onClose={() => setSigning(false)}
                />
            )}
        </DetailSection>
    );
};

export default ClientTravelSection;
